import React, { useState, useEffect, useRef } from 'react';
import { Plus, Edit, Trash2, X, List } from 'lucide-react';
import * as api from '../services/api';

function CustomCategories() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showItemsModal, setShowItemsModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [categoryItems, setCategoryItems] = useState([]);
  const [availableItems, setAvailableItems] = useState([]);
  const [itemSearch, setItemSearch] = useState('');
  const [itemType, setItemType] = useState('channel');

  const [formData, setFormData] = useState({
    name: '',
    type: 'channel',
    description: '',
    sort_order: 0,
    enabled: 1
  });

  useEffect(() => {
    loadCategories();
  }, []);

  const loadCategories = async () => {
    try {
      setLoading(true);
      const res = await api.getCustomCategories();
      setCategories(Array.isArray(res.data) ? res.data : []);
    } catch (error) {
      console.error('Failed to load custom categories:', error);
      setCategories([]);
    } finally {
      setLoading(false);
    }
  };

  const openCreateModal = () => {
    setFormData({
      name: '',
      type: 'channel',
      description: '',
      sort_order: 0,
      enabled: 1
    });
    setShowCreateModal(true);
  };

  const openEditModal = (category) => {
    setEditingCategory(category);
    setFormData({
      name: category.name,
      type: category.type,
      description: category.description || '',
      sort_order: category.sort_order || 0,
      enabled: category.enabled
    });
    setShowEditModal(true);
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await api.createCustomCategory(formData);
      setShowCreateModal(false);
      loadCategories();
    } catch (error) {
      alert('Failed to create category: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    try {
      await api.updateCustomCategory(editingCategory.id, formData);
      setShowEditModal(false);
      loadCategories();
    } catch (error) {
      alert('Failed to update category: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this custom category? All items will be removed from it.')) {
      return;
    }
    try {
      await api.deleteCustomCategory(id);
      loadCategories();
    } catch (error) {
      alert('Failed to delete category: ' + (error.response?.data?.error || error.message));
    }
  };

  const openItemsModal = async (category) => {
    setSelectedCategory(category);
    setItemType('channel');
    try {
      const [itemsRes, availableRes] = await Promise.all([
        api.getCustomCategoryItems(category.id),
        api.getAvailableItems(category.id, 'channel', '')
      ]);
      setCategoryItems(itemsRes.data.items || []);
      setAvailableItems(Array.isArray(availableRes.data) ? availableRes.data : []);
    } catch (error) {
      console.error('Failed to load items:', error);
    }
    setShowItemsModal(true);
  };

  const loadAvailableItems = async (type, search = '') => {
    if (!selectedCategory) return;
    try {
      const res = await api.getAvailableItems(selectedCategory.id, type, search);
      setAvailableItems(Array.isArray(res.data) ? res.data : []);
    } catch (error) {
      console.error('Failed to load available items:', error);
      setAvailableItems([]);
    }
  };

  const handleItemTypeChange = async (type) => {
    setItemType(type);
    setItemSearch('');
    await loadAvailableItems(type, '');
  };

  const searchTimer = useRef(null);

  const handleSearchChange = (search) => {
    setItemSearch(search);
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      loadAvailableItems(itemType, search);
    }, 400);
  };

  const addItemToCategory = async (item) => {
    try {
      await api.addItemsToCategory(selectedCategory.id, [{ item_id: item.id, item_type: itemType }]);
      // Reload items
      const itemsRes = await api.getCustomCategoryItems(selectedCategory.id);
      setCategoryItems(itemsRes.data.items || []);
      // Reload available items
      await loadAvailableItems(itemType, itemSearch);
    } catch (error) {
      alert('Failed to add item: ' + (error.response?.data?.error || error.message));
    }
  };

  const removeItemFromCategory = async (mappingId) => {
    try {
      await api.removeItemFromCategory(selectedCategory.id, mappingId);
      // Reload items
      const itemsRes = await api.getCustomCategoryItems(selectedCategory.id);
      setCategoryItems(itemsRes.data.items || []);
      // Reload available items
      await loadAvailableItems(itemType, itemSearch);
    } catch (error) {
      alert('Failed to remove item: ' + (error.response?.data?.error || error.message));
    }
  };

  if (loading) {
    return <div className="loading">Loading custom categories...</div>;
  }

  return (
    <div>
      <div className="page-header">
        <h2>Custom Categories</h2>
        <p>Create custom groups for channels, movies, and series that appear in IPTV apps</p>
      </div>

      <div className="table-container" style={{ marginBottom: '20px' }}>
        <div className="table-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3>Categories ({categories.length})</h3>
          <button className="btn btn-primary" onClick={openCreateModal}>
            <Plus size={16} />
            Create Category
          </button>
        </div>

        {categories.length === 0 ? (
          <div className="empty-state">
            <h3>No custom categories yet</h3>
            <p>Create your first custom category to organize your content</p>
          </div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Type</th>
                <th>Items</th>
                <th>Sort Order</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {categories.map((category) => (
                <tr key={category.id}>
                  <td><strong>{category.name}</strong></td>
                  <td>
                    <span className="badge badge-info">
                      {category.type}
                    </span>
                  </td>
                  <td>{category.item_count || 0}</td>
                  <td>{category.sort_order}</td>
                  <td>
                    <span className={`badge badge-${category.enabled ? 'success' : 'danger'}`}>
                      {category.enabled ? 'Enabled' : 'Disabled'}
                    </span>
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button
                        onClick={() => openItemsModal(category)}
                        className="btn btn-primary"
                        style={{ padding: '6px 12px' }}
                        title="Manage Items"
                      >
                        <List size={14} />
                      </button>
                      <button
                        onClick={() => openEditModal(category)}
                        className="btn btn-secondary"
                        style={{ padding: '6px 12px' }}
                        title="Edit Category"
                      >
                        <Edit size={14} />
                      </button>
                      <button
                        onClick={() => handleDelete(category.id)}
                        className="btn btn-danger"
                        style={{ padding: '6px 12px' }}
                        title="Delete Category"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.5)', display: 'flex',
          alignItems: 'center', justifyContent: 'center', zIndex: 1000
        }}>
          <div style={{
            background: 'white', padding: '30px', borderRadius: '8px',
            width: '100%', maxWidth: '500px'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '20px' }}>
              <h3>Create Custom Category</h3>
              <button onClick={() => setShowCreateModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
                <X size={24} />
              </button>
            </div>
            <form onSubmit={handleCreate}>
              <div className="form-group">
                <label>Category Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  placeholder="e.g., My Favorites, Kids Shows"
                />
              </div>
              <div className="form-group">
                <label>Type *</label>
                <select
                  value={formData.type}
                  onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                  required
                >
                  <option value="channel">Channels Only</option>
                  <option value="vod">Movies Only</option>
                  <option value="series">Series Only</option>
                  <option value="mixed">Mixed (All Types)</option>
                </select>
              </div>
              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                  placeholder="Optional description"
                />
              </div>
              <div className="form-group">
                <label>Sort Order</label>
                <input
                  type="number"
                  value={formData.sort_order}
                  onChange={(e) => setFormData({ ...formData, sort_order: parseInt(e.target.value) })}
                />
              </div>
              <div className="form-group">
                <label>
                  <input
                    type="checkbox"
                    checked={formData.enabled === 1}
                    onChange={(e) => setFormData({ ...formData, enabled: e.target.checked ? 1 : 0 })}
                    style={{ marginRight: '8px' }}
                  />
                  Enabled
                </label>
              </div>
              <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
                <button type="submit" className="btn btn-primary" style={{ flex: 1 }}>
                  Create Category
                </button>
                <button type="button" className="btn btn-secondary" style={{ flex: 1 }} onClick={() => setShowCreateModal(false)}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && editingCategory && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.5)', display: 'flex',
          alignItems: 'center', justifyContent: 'center', zIndex: 1000
        }}>
          <div style={{
            background: 'white', padding: '30px', borderRadius: '8px',
            width: '100%', maxWidth: '500px'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '20px' }}>
              <h3>Edit Category: {editingCategory.name}</h3>
              <button onClick={() => setShowEditModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
                <X size={24} />
              </button>
            </div>
            <form onSubmit={handleUpdate}>
              <div className="form-group">
                <label>Category Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Type *</label>
                <select
                  value={formData.type}
                  onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                  required
                >
                  <option value="channel">Channels Only</option>
                  <option value="vod">Movies Only</option>
                  <option value="series">Series Only</option>
                  <option value="mixed">Mixed (All Types)</option>
                </select>
              </div>
              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                />
              </div>
              <div className="form-group">
                <label>Sort Order</label>
                <input
                  type="number"
                  value={formData.sort_order}
                  onChange={(e) => setFormData({ ...formData, sort_order: parseInt(e.target.value) })}
                />
              </div>
              <div className="form-group">
                <label>
                  <input
                    type="checkbox"
                    checked={formData.enabled === 1}
                    onChange={(e) => setFormData({ ...formData, enabled: e.target.checked ? 1 : 0 })}
                    style={{ marginRight: '8px' }}
                  />
                  Enabled
                </label>
              </div>
              <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
                <button type="submit" className="btn btn-primary" style={{ flex: 1 }}>
                  Save Changes
                </button>
                <button type="button" className="btn btn-secondary" style={{ flex: 1 }} onClick={() => setShowEditModal(false)}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Items Modal */}
      {showItemsModal && selectedCategory && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.5)', display: 'flex',
          alignItems: 'center', justifyContent: 'center', zIndex: 1000
        }}>
          <div style={{
            background: 'white', padding: '30px', borderRadius: '8px',
            width: '100%', maxWidth: '900px', maxHeight: '90vh', overflow: 'auto'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '20px' }}>
              <h3>Manage Items: {selectedCategory.name}</h3>
              <button onClick={() => setShowItemsModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
                <X size={24} />
              </button>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
              {/* Current Items */}
              <div>
                <h4>Current Items ({categoryItems.length})</h4>
                <div style={{ maxHeight: '400px', overflow: 'auto', border: '1px solid #e5e7eb', borderRadius: '6px', padding: '10px' }}>
                  {categoryItems.length === 0 ? (
                    <p style={{ textAlign: 'center', color: '#9ca3af' }}>No items yet</p>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                      {categoryItems.map((item) => (
                        <div key={item.mapping_id} style={{
                          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                          padding: '10px', background: '#f9fafb', borderRadius: '6px'
                        }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            {item.image && (
                              <img src={item.image} alt="" style={{ width: '40px', height: '40px', borderRadius: '4px', objectFit: 'cover' }} />
                            )}
                            <div>
                              <strong>{item.name}</strong>
                              <div style={{ fontSize: '12px', color: '#6b7280' }}>{item.item_type}</div>
                            </div>
                          </div>
                          <button
                            onClick={() => removeItemFromCategory(item.mapping_id)}
                            className="btn btn-danger"
                            style={{ padding: '4px 8px' }}
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Available Items */}
              <div>
                <h4>Add Items</h4>
                <div style={{ marginBottom: '10px' }}>
                  <select
                    value={itemType}
                    onChange={(e) => handleItemTypeChange(e.target.value)}
                    style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #d1d5db', marginBottom: '10px' }}
                  >
                    <option value="channel">Channels</option>
                    <option value="vod">Movies</option>
                    <option value="series">Series</option>
                  </select>
                  <input
                    type="text"
                    placeholder={`Search ${itemType}...`}
                    value={itemSearch}
                    onChange={(e) => handleSearchChange(e.target.value)}
                    style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #d1d5db' }}
                  />
                </div>
                <div style={{ maxHeight: '400px', overflow: 'auto', border: '1px solid #e5e7eb', borderRadius: '6px', padding: '10px' }}>
                  {availableItems.length === 0 ? (
                    <p style={{ textAlign: 'center', color: '#9ca3af' }}>No items found</p>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                      {availableItems.map((item) => (
                        <div key={item.id} style={{
                          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                          padding: '10px', background: '#f9fafb', borderRadius: '6px'
                        }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            {item.image && (
                              <img src={item.image} alt="" style={{ width: '40px', height: '40px', borderRadius: '4px', objectFit: 'cover' }} />
                            )}
                            <div>
                              <strong>{item.name}</strong>
                              <div style={{ fontSize: '12px', color: '#6b7280' }}>
                                {item.category}
                                {item.provider_name && (
                                  <span style={{ marginLeft: '8px', padding: '1px 6px', background: '#e0e7ff', color: '#3730a3', borderRadius: '4px', fontSize: '11px' }}>
                                    {item.provider_name}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                          <button
                            onClick={() => addItemToCategory(item)}
                            className="btn btn-primary"
                            style={{ padding: '4px 8px' }}
                          >
                            <Plus size={14} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default CustomCategories;
